import { Injectable } from '@angular/core'
import { DepotoCore } from 'depoto-core/DepotoCore'
import { ServiceContainer } from 'depoto-core/src/interfaces'
import { OptionsConfig } from '../configs'

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  private core: DepotoCore
  constructor() {
    this.core = new DepotoCore(OptionsConfig.strings.app_prefix, null, null, null)
  }

  get services(): ServiceContainer {
    return this.core.services
  }

  /**
   * Legacy cleanup helper. It is currently unused for login/logout.
   *
   * Do not re-enable this as login cleanup without revisiting the deletion policy: the login branch deletes other
   * users' email-suffixed view settings. If legacy non-email-scoped view settings should not be shared between
   * users, migrate those view keys to include the user email instead of clearing saved settings on login.
   */
  cleanLocalStorage(email: string, log: 'login' | 'logout'): void {
    try {
      if (!('localStorage' in window)) return

      const appPrefix = OptionsConfig.strings.app_prefix
      const baseListingKey = this.services.storage.keys.views.baseListing.key

      const isAuthNamespace = (key: string) => key.startsWith(appPrefix + 'auth_')
      const isUserRoles = (key: string) => key.endsWith('_user_roles')
      const isBaseListingForCurrentUser = (key: string) => {
        const base = appPrefix + baseListingKey
        if (!email) return false
        return key === `${base}.${email}` || key.startsWith(`${base}.${email}.`)
      }
      const isAnyBaseListing = (key: string) => key.startsWith(appPrefix + baseListingKey + '.')

      for (const key of Object.keys(localStorage)) {
        const authKey = isAuthNamespace(key)

        // On logout: wipe global view/ui caches (non user-suffixed), keep auth keys to let oauth finish its flow
        if (log === 'logout') {
          // Remove everything under this app prefix that is NOT auth related and is not explicitly preserved
          if (key.startsWith(appPrefix) && !authKey && !isUserRoles(key)) {
            localStorage.removeItem(key)
          }
          continue
        }

        // On login: remove stale caches for previous user + global view caches, but keep auth keys and base listing settings
        if (log === 'login') {
          const isAppKey = key.startsWith(appPrefix)
          // Treat keys as user-specific only if they are our app keys and clearly suffixed with .<email>
          const hasCurrentEmailSuffix = !!email && (key.endsWith(`.${email}`) || key.includes(`.${email}.`))
          const isUserSpecific = isAppKey && /\.[^.]+@[^.]+\.[^.]+(\.|$)/.test(key) // simplistic email suffix detector

          // Remove per-user caches that belong to previous user (i.e., not the just-logged-in one)
          if (isUserSpecific && !hasCurrentEmailSuffix) {
            // Preserve base listing for current user only; delete other users' including their BaseListing
            if (!isBaseListingForCurrentUser(key)) localStorage.removeItem(key)
            continue
          }

          // Remove global caches except auth keys and roles
          if (
            (!isUserSpecific && !authKey && !isUserRoles(key)) ||
            (isAnyBaseListing(key) && !isBaseListingForCurrentUser(key))
          ) {
            localStorage.removeItem(key)
            continue
          }
        }
      }
    } catch (e) {
      // swallow storage access errors (Safari private mode, etc.)
      console.warn('cleanLocalStorage failed', e)
    }
  }
}
